/**
 * Remembers a table's page-size choice for the rest of the browser session, keyed by the table's
 * `tableId`. Shared by `DataTableComponent` (which displays the size) and the server-side parents
 * that issue the first fetch - both must read the same value, or the parent fetches one page size
 * while the footer reports another.
 */

const PAGE_SIZE_STORAGE_PREFIX = 'dt.pageSize.';

/** Reads the remembered page size for a table, or null when there is none. */
export function readRememberedPageSize(tableId: string): number | null {
  if (!tableId) {
    return null;
  }
  try {
    const stored = sessionStorage.getItem(PAGE_SIZE_STORAGE_PREFIX + tableId);
    const size = stored ? Number(stored) : 0;
    return size > 0 ? size : null;
  } catch {
    // sessionStorage can throw when storage is disabled. Remembering the size is a convenience,
    // never a requirement, so fall back to the configured size.
    return null;
  }
}

/** Stores the chosen page size for a table for the rest of the browser session. */
export function rememberPageSize(tableId: string, pageSize: number): void {
  if (!tableId) {
    return;
  }
  try {
    sessionStorage.setItem(PAGE_SIZE_STORAGE_PREFIX + tableId, String(pageSize));
  } catch {
    // Ignore - see readRememberedPageSize.
  }
}
